import { useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";

import { getTheme } from "./theme";
import ColorModeContext from "./ColorModeContext";
import MainLayout from "./layouts/MainLayout";

import { AuthProvider } from "./auth/AuthContext";
import { ProtectedRoute, RequireModule, RequireCentral } from "./auth/ProtectedRoute";
import Login from "./pages/Login";
import SolicitarCadastro from "./pages/SolicitarCadastro";
import SemPermissao from "./pages/SemPermissao";
import SolicitacoesList from "./pages/solicitacoes/List";

import Home from "./pages";

// Funcionários
import FuncionariosList from "./pages/funcionarios/List";
import FuncionarioForm from "./pages/funcionarios/Form";
import FuncionarioEdit from "./pages/funcionarios/Edit";

// Equipes
import EquipesList from "./pages/equipes/List";
import EquipeForm from "./pages/equipes/Form";
import EquipeEdit from "./pages/equipes/Edit";

// Fornecedores
import FornecedoresList from "./pages/fornecedores/List";
import FornecedorForm from "./pages/fornecedores/Form";
import FornecedorEdit from "./pages/fornecedores/Edit";

// Almoxarifados
import AlmoxarifadosList from "./pages/almoxarifados/List";
import AlmoxarifadoDetalhes from "./pages/almoxarifados/Detalhes";
import AlmoxarifadoForm from "./pages/almoxarifados/Form";
import AlmoxarifadoEdit from "./pages/almoxarifados/Edit";

// Saidas
import SaidasList from "./pages/saidas/List";
import SaidaForm from "./pages/saidas/Form";
import SaidaEdit from "./pages/saidas/Edit";

// Compras
import ListCompras from './pages/compras/List';
import FormCompras from './pages/compras/Form';
import EditCompras from './pages/compras/Edit';
import ServicosList from "./pages/servicos/List";
import ServicoForm from "./pages/servicos/Form";
import ServicoEdit from "./pages/servicos/Edit";
import GastosAplicacoesList from "./pages/gastos-aplicacoes/List";

// Produtos

import ProdutosList from "./pages/produtos/List"
import ProdutoForm from "./pages/produtos/Form"
import ProdutoEdit from "./pages/produtos/Edit"

// Equipamentos
import EquipamentosList from "./pages/equipamentos/List"
import EquipamentoForm from "./pages/equipamentos/Form"
import EquipamentoEdit from "./pages/equipamentos/Edit"
import ManutencaoList from "./pages/manutencao/List"
import ManutencaoDetalhes from "./pages/manutencao/Detalhes"
import AcompanhamentoEquipamentos from "./pages/acompanhamento-equipamentos"
import AlojamentosList from "./pages/alojamentos/List"
import AlojamentoForm from "./pages/alojamentos/Form"
import AlojamentoDetalhes from "./pages/alojamentos/Detalhes"

function App() {
  // Modo do tema, persistido no navegador (mantém a escolha ao recarregar).
  const [mode, setMode] = useState(
    () => localStorage.getItem("colorMode") || "dark"
  );

  const colorMode = useMemo(
    () => ({
      mode,
      toggle: () =>
        setMode((anterior) => {
          const novo = anterior === "dark" ? "light" : "dark";
          localStorage.setItem("colorMode", novo);
          return novo;
        })
    }),
    [mode]
  );

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Rotas públicas */}
            <Route path="/login" element={<Login />} />
            <Route path="/solicitar-cadastro" element={<SolicitarCadastro />} />

            {/* Área autenticada: MainLayout com <Outlet/>, protegida por login */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Home />} />
              <Route path="/sem-permissao" element={<SemPermissao />} />

              {/* Solicitações de cadastro (somente CENTRAL) */}
              <Route path="/solicitacoes" element={<RequireCentral><SolicitacoesList /></RequireCentral>} />

              {/*Rotas Funcionarios*/}
              <Route path="/funcionarios" element={<RequireModule modulo="funcionarios"><FuncionariosList /></RequireModule>} />
              <Route path="/funcionarios/cadastro" element={<RequireModule modulo="funcionarios" acao="editar"><FuncionarioForm /></RequireModule>} />
              <Route path="/funcionarios/:id/editar" element={<RequireModule modulo="funcionarios" acao="editar"><FuncionarioEdit /></RequireModule>} />

              {/*Rotas Equipes*/}
              <Route path="/equipes" element={<RequireModule modulo="equipes"><EquipesList /></RequireModule>} />
              <Route path="/equipes/cadastro" element={<RequireModule modulo="equipes" acao="editar"><EquipeForm /></RequireModule>} />
              <Route path="/equipes/:id/editar" element={<RequireModule modulo="equipes" acao="editar"><EquipeEdit /></RequireModule>} />

              {/*Rotas Fornecedores*/}
              <Route path="/fornecedores" element={<RequireModule modulo="fornecedores"><FornecedoresList /></RequireModule>} />
              <Route path="/fornecedores/cadastro" element={<RequireModule modulo="fornecedores" acao="editar"><FornecedorForm /></RequireModule>} />
              <Route path="/fornecedores/:id/editar" element={<RequireModule modulo="fornecedores" acao="editar"><FornecedorEdit /></RequireModule>} />

              {/*Rotas Almoxarifado*/}
              <Route path="/almoxarifados" element={<RequireModule modulo="almoxarifados"><AlmoxarifadosList /></RequireModule>} />
              <Route path="/almoxarifados/cadastro" element={<RequireModule modulo="almoxarifados" acao="editar"><AlmoxarifadoForm /></RequireModule>} />
              <Route path="/almoxarifados/:id/editar" element={<RequireModule modulo="almoxarifados" acao="editar"><AlmoxarifadoEdit /></RequireModule>} />
              <Route path="/almoxarifados/:id" element={<RequireModule modulo="almoxarifados"><AlmoxarifadoDetalhes /></RequireModule>} />

              {/*Rotas Saidas*/}
              <Route path="/saidas" element={<RequireModule modulo="saidas"><SaidasList /></RequireModule>} />
              <Route path="/saidas/cadastro" element={<RequireModule modulo="saidas" acao="editar"><SaidaForm /></RequireModule>} />
              <Route path="/saidas/:id/editar" element={<RequireModule modulo="saidas" acao="editar"><SaidaEdit /></RequireModule>} />

              {/*Rotas Compras*/}
              <Route path="/compras" element={<RequireModule modulo="compras"><ListCompras /></RequireModule>} />
              <Route path="/compras/cadastro" element={<RequireModule modulo="compras" acao="editar"><FormCompras /></RequireModule>} />
              <Route path="/compras/:id/editar" element={<RequireModule modulo="compras" acao="editar"><EditCompras /></RequireModule>} />

              <Route path="/servicos" element={<RequireModule modulo="servicos"><ServicosList /></RequireModule>} />
              <Route path="/servicos/cadastro" element={<RequireModule modulo="servicos" acao="editar"><ServicoForm /></RequireModule>} />
              <Route path="/servicos/:id/editar" element={<RequireModule modulo="servicos" acao="editar"><ServicoEdit /></RequireModule>} />
              <Route path="/gastos-aplicacoes" element={<RequireModule modulo="gastos_aplicacoes"><GastosAplicacoesList /></RequireModule>} />
              <Route path="/alojamentos" element={<RequireModule modulo="alojamentos"><AlojamentosList /></RequireModule>} />
              <Route path="/alojamentos/cadastro" element={<RequireModule modulo="alojamentos" acao="editar"><AlojamentoForm /></RequireModule>} />
              <Route path="/alojamentos/:id/editar" element={<RequireModule modulo="alojamentos" acao="editar"><AlojamentoForm /></RequireModule>} />
              <Route path="/alojamentos/:id" element={<RequireModule modulo="alojamentos"><AlojamentoDetalhes /></RequireModule>} />

              {/*Rotas Produtos*/}
              <Route path="/produtos" element={<RequireModule modulo="produtos"><ProdutosList /></RequireModule>} />
              <Route path="/produtos/novo" element={<RequireModule modulo="produtos" acao="editar"><ProdutoForm /></RequireModule>} />
              <Route path="/produtos/editar/:id" element={<RequireModule modulo="produtos" acao="editar"><ProdutoEdit /></RequireModule>} />

              {/* Rotas Equipamentos */}
              <Route path="/administracao/acompanhamento-equipamentos" element={<RequireModule modulo="acompanhamento_equipamentos"><AcompanhamentoEquipamentos /></RequireModule>} />
              <Route path="/equipamentos" element={<RequireModule modulo="equipamentos"><EquipamentosList /></RequireModule>} />
              <Route path="/equipamentos/cadastro" element={<RequireModule modulo="equipamentos" acao="editar"><EquipamentoForm /></RequireModule>} />
              <Route path="/equipamentos/manutencao" element={<RequireModule modulo="equipamentos"><ManutencaoList /></RequireModule>} />
              <Route path="/equipamentos/:id/manutencao" element={<RequireModule modulo="equipamentos"><ManutencaoDetalhes /></RequireModule>} />
              <Route path="/equipamentos/:id/editar" element={<RequireModule modulo="equipamentos" acao="editar"><EquipamentoEdit /></RequireModule>} />
            </Route>

            {/* Qualquer outra rota → início (ou login, se não autenticado) */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
